import React, { Suspense, useMemo } from 'react'
import ReactDOM from 'react-dom'
import '@fs/react-scripts/polyfills'
import * as Sentry from '@sentry/browser'
import Root, { FrontierBaseProviders } from '@fs/zion-root'
import { AppLayout } from '@fs/zion-ui'
import { Router } from '@fs/zion-router'
import { appPath, sentryDSN, mergeSentryConfig } from '@fs/zion-config'
import { setAppLocales, essentialLocales } from '@fs/zion-locale'
import { NoticeLoading } from '@fs/zion-icon'
import App from './App'
import * as serviceWorkerRegistration from './serviceWorkerRegistration'
import './locales'
import RootConfigProvider, { useRootConfig } from './RootConfigProvider'
import { ZION_CMS_SITES } from './constants'

const BrightspotProvider = React.lazy(() =>
  import('@fs/zion-cms').then((module) => ({ default: module.BrightspotProvider }))
)

// Enable local feature flag development using the file "dev.flags.js"
// If that file exports anything truthy it will initialize feature flags in localhost mode
// and use the flags defined in that file, instead of flags defined in split.
const mockFlags = process.env.NODE_ENV === 'development' ? import('./dev.flags').then((m) => m.default) : undefined
const mockActivities =
  process.env.NODE_ENV === 'development' ? import('./dev.activities').then((m) => m.default) : undefined

// initialize Sentry for the app
if (sentryDSN) {
  // pass Sentry overrides to mergeSentryConfig
  Sentry.init(
    mergeSentryConfig({
      dsn: sentryDSN,
      ignoreErrors: [
        // Errors that are not actionable, potentially caused by Outlook safe links
        // https://github.com/getsentry/sentry-javascript/issues/3440
        /Non-Error promise rejection captured with value: Object Not Found Matching Id:[0-9]+, MethodName:(browserNotificationOpenUi|soundPlayingChanged)/,
        // No information comes through with this error
        // the only thing that was partially consistent was that a lot of browsers had
        // `navigator.doNotTrack is enabled on the browser` logged
        /UnknownError: Internal error/i,
        // Errors that are not actionable until more info is given, looks to be caused by a known issue in chrome.
        // https://issues.chromium.org/issues/342779913
        /DataError: Failed to read large IndexedDB value/,
        // Browser-level IndexedDB errors that are not actionable - HOME-PORTAL-KF9
        /InvalidStateError: An attempt was made to use an object that is not, or is no longer, usable/,
        // Browser serialization errors that are not actionable - HOME-PORTAL-KJW
        /InternalError: bad serialized structured data/,
      ],
      denyUrls: [
        // Error caused by qualtrics
        // Loading chunk 12 failed. (error: https://siteintercept.qualtrics.com/dxjsmodule/12.ebe7e89e19ae15a2ea2c.chunk.js?Q_CLIENTVERSION=2.9.0&Q_CLIENTTYPE=web&Q_BRANDID=www.familysearch.org)
        /https?:\/\/.*siteintercept\.qualtrics\.com/,
      ],
    })
  )
}

// Here we pass the mounted app path from the server to the client.
const base = appPath ? new URL(appPath).pathname : ''

const RootWrapper = () => {
  const { showFooter } = useRootConfig()
  const footer = useMemo(() => ({ hidden: !showFooter }), [showFooter])
  return (
    <Root analytics={!navigator.userAgent.includes('prerender')} footer={footer}>
      <AppLayout fullWidth>
        <App />
      </AppLayout>
    </Root>
  )
}

// Add any app logic here as flags, languages and translations are now configured and available
const FrontierWrapper = () => {
  setAppLocales(essentialLocales)

  return (
    <BrightspotProvider sites={ZION_CMS_SITES}>
      <Router basename={base}>
        <RootConfigProvider>
          <RootWrapper />
        </RootConfigProvider>
      </Router>
    </BrightspotProvider>
  )
}

// Do not make changes to FrontierRoot, all changes should be made to FrontierWrapper
const FrontierRoot = () => (
  <React.StrictMode>
    <Suspense fallback={<NoticeLoading />}>
      <FrontierBaseProviders mockFlags={mockFlags} mockActivities={mockActivities}>
        <FrontierWrapper />
      </FrontierBaseProviders>
    </Suspense>
  </React.StrictMode>
)

ReactDOM.render(<FrontierRoot />, document.getElementById('root'))

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://cra.link/PWA
serviceWorkerRegistration.unregister()
